import React, { useCallback, useMemo, useState } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { getInputPlaceholderPlugin, insertNode, selectionType } from '@lyearn/lygo-blocks';
import { EditorState, EditorView, NewEditor } from '@lyearn/lygo-core';
import { AddBlock } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import { PlainEditorProps } from '@/components/Editor/PlainEditor/types';
import { fillInTheBlanksExtensions } from '@/lygo/FillInTheBlanks';
import { QuestionEditorViewModes } from '@/modules/question/types';
import { FtbComponentInput } from '@/types/schema';

import BlankBox from '../FtbPlayer/components/DropdownPlayer/components/BlankBox';
import FtbPlayerContainer from '../FtbPlayer/components/FtbPlayerContainer';
import { addBlankStyles } from './styles';
import { getAdaptedContent, getContentJSON } from './utils';

interface RichTextEditorProps extends Omit<PlainEditorProps, 'value' | 'onChangeText'> {
  ftbComponents?: Array<FtbComponentInput>;
  onChangeFtb: (content: Array<FtbComponentInput>, inputHtml: string) => void;
  viewMode: QuestionEditorViewModes;
}

function RichTextEditor(props: RichTextEditorProps) {
  const {
    className,
    variant = 'body-short-01',
    ftbComponents,
    onChangeFtb,
    placeholder,
    viewMode,
  } = props;
  const { t } = useTranslation();
  const [editorView, setEditorView] = useState<EditorView | undefined>();

  const onEditorChange = useEvent((_: EditorState, jsonValue: any) => {
    const editorContent = jsonValue.content[0]?.content;

    const { questionTextWithBlank, adaptedContent } = getAdaptedContent(editorContent);
    onChangeFtb?.(adaptedContent, questionTextWithBlank);
  });

  const onAddBlank = useCallback(() => {
    if (!editorView) {
      return;
    }

    const position = editorView.state.selection.$anchor.pos;
    insertNode(
      editorView,
      selectionType.NODE,
      editorView.state.schema.nodes.blank.create(),
      position,
    );
  }, [editorView]);

  const memoizedValue = useMemo(() => {
    return getContentJSON(ftbComponents);
  }, [ftbComponents]);

  return (
    <Box className={cn('flex-1', variant, className)}>
      {viewMode !== 'create' ? (
        <FtbPlayerContainer>
          {(ftbComponents || []).map((ftbComponent, index) => {
            if (ftbComponent.isBlank) {
              return (
                <BlankBox
                  key={`${ftbComponent.text}-${index}`}
                  className="border border-field"
                  text={ftbComponent.text || ''}
                />
              );
            }
            return (
              <Box key={`${ftbComponent.text}-${index}`} className="me-16" component="span">
                {ftbComponent.text}
              </Box>
            );
          })}
        </FtbPlayerContainer>
      ) : (
        <>
          <NewEditor
            extensions={fillInTheBlanksExtensions}
            jsonValue={memoizedValue}
            keymaps={{
              // this is first keymap, returning true will disable Enter key in editor
              Enter: () => true,
            }}
            plugins={[
              () => getInputPlaceholderPlugin(placeholder ?? t('QuizCreator:QuestionPlaceholder')),
            ]}
            onChangeJSON={onEditorChange}
            onCreateEditorView={setEditorView}
          />
          <AddBlock
            className={cn('mt-32', addBlankStyles)}
            title={t('QuizCreator:AddBlank')}
            onClick={onAddBlank}
          />
        </>
      )}
    </Box>
  );
}

export default RichTextEditor;
